import { getRouteAnalyticsContext } from './route-analytics-context.js';

import type { AnyRouter } from '@tanstack/react-router';
import type { PostHog } from 'posthog-js';

/**
 * Wires PostHog `$pageview` capture to the router. Autocapture pageviews are
 * disabled for this SPA (`capture_pageview: false`), so this is the sole source
 * of pageview events.
 *
 * Each resolved navigation emits one `$pageview` tagged with the matched leaf
 * route id (with `$param` placeholders intact, e.g.
 * `/…/revisions/$revisionId/results`). That low-cardinality id lets pageviews
 * be grouped by page — such as the results page — without fragmenting across
 * the UUIDs embedded in the concrete URL. Common path params and the results
 * tab are attached as structured properties for segmentation.
 *
 * @param router - The app router to observe.
 * @param client - The PostHog client to capture events on.
 * @returns An unsubscribe function.
 */
export function registerPageviewTracking(router: AnyRouter, client: PostHog): () => void {
	// Guards against the initial-load safety call below double-firing with the
	// first `onResolved` for the same URL. Only suppresses back-to-back
	// duplicates, so A → B → A still emits three pageviews.
	let lastCapturedUrl: string | undefined;

	const capturePageview = () => {
		const matches = router.state.matches;
		const leaf = matches[matches.length - 1];
		if (!leaf) {
			return;
		}

		const url = window.location.href;
		if (url === lastCapturedUrl) {
			return;
		}
		lastCapturedUrl = url;

		const params = leaf.params as Record<string, string | undefined>;
		const search = leaf.search as Record<string, unknown>;

		client.capture('$pageview', {
			$current_url: url,
			route_id: leaf.routeId,
			...getRouteAnalyticsContext(router),
			...(typeof params['projectId'] === 'string' ? { project_id: params['projectId'] } : {}),
			...(typeof search['tab'] === 'string' ? { results_tab: search['tab'] } : {}),
			...(typeof search['planId'] === 'string' ? { wizard_plan_id: search['planId'] } : {}),
		});
	};

	// Fire for the initial load in case the router resolved it before this
	// subscription attached, then for every subsequent navigation.
	capturePageview();
	return router.subscribe('onResolved', capturePageview);
}
